import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppDispatch } from '../../redux/hooks';
import { setGuestMode } from '../../redux/slice/authModalSlice';
import { setProfile } from '../../redux/slice/playerProfileSlice';
import { Images } from '../../utils/imageConstants';
import {
  setAuthToken,
  setIdToken,
  setRefreshToken,
  clearGuestMode,
  decodeAuthTokenPayload,
  consumeSocialAuthRedirect,
} from '../../helper/helper';
import { consumeStoredPkceState, exchangeCodeForTokens } from '../../services/cognitoOAuth';
import { fetchExistingPlayerProfile, createSocialPlayer } from './api/socialAuthApi';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const SocialAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const oauthError = params.get('error');
      const code = params.get('code');
      const returnedState = params.get('state');
      const { verifier, state: storedState } = consumeStoredPkceState();
      // Always consume exactly once per run, win or lose, so a stale value never leaks into a
      // later unrelated sign-in.
      const redirectTo = consumeSocialAuthRedirect();

      // User cancelled/denied on the Hosted UI — reset silently, no error toast.
      if (oauthError) {
        navigate('/', { replace: true });
        return;
      }

      if (!code || !verifier || !returnedState || returnedState !== storedState) {
        toast.error(GENERIC_ERROR);
        navigate('/', { replace: true });
        return;
      }

      try {
        const tokens = await exchangeCodeForTokens(code, verifier);
        const idClaims = decodeAuthTokenPayload(tokens.id_token);
        const sub = idClaims?.sub as string | undefined;
        if (!sub) {
          throw new Error('Missing sub claim in id_token');
        }
        const email = (idClaims?.email as string | undefined) ?? null;
        const givenName = (idClaims?.given_name as string | undefined) ?? '';
        const familyName = (idClaims?.family_name as string | undefined) ?? '';

        setAuthToken(tokens.access_token, true);
        setIdToken(tokens.id_token, true);
        if (tokens.refresh_token) setRefreshToken(tokens.refresh_token, true);
        dispatch(setGuestMode(false));
        clearGuestMode();

        const existingProfile = await fetchExistingPlayerProfile(sub, tokens.access_token);

        if (!existingProfile) {
          try {
            await createSocialPlayer({
              userName: email ?? sub,
              playerId: sub,
              emailId: email,
              phoneNo: null,
              countryCode: null,
            });
          } catch {
            toast.error(GENERIC_ERROR);
            navigate('/', { replace: true });
            return;
          }
          navigate('/complete-profile', {
            replace: true,
            state: { email: email ?? '', userId: sub, firstName: givenName, lastName: familyName, redirectTo },
          });
          return;
        }

        const player = existingProfile.playerResponse;
        const isIncomplete = player?.userStatus === 'CREATED' || !player?.playerFirstName;
        if (isIncomplete) {
          navigate('/complete-profile', {
            replace: true,
            state: {
              email: player?.emailId || email || '',
              userId: sub,
              mobileNumber: player?.phoneNo || undefined,
              countryCode: player?.countryCode || undefined,
              // Apple only sends the name on the very first authorization; fall back to our
              // saved record on later logins rather than expecting Apple to resend it.
              firstName: player?.playerFirstName || givenName,
              lastName: player?.playerLastName || familyName,
              redirectTo,
            },
          });
          return;
        }

        dispatch(setProfile(existingProfile));
        toast.success('Signed in successfully');
        navigate(redirectTo, { replace: true });
      } catch (err) {
        console.error('Social sign-in failed:', err);
        toast.error(GENERIC_ERROR);
        navigate('/', { replace: true });
      }
    };

    void run();
  }, [dispatch, navigate]);

  return (
    <div className="figma-login-container">
      <div className="login-background-image">
        <div className="login-modal-container social-auth-callback">
          <div className="login-header">
            <div className="logo-container">
              <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
            </div>
            <h1 className="app-name">CRICCENTER</h1>
          </div>
          <p className="social-auth-callback-message">Signing you in…</p>
        </div>
      </div>
    </div>
  );
};

export default SocialAuthCallback;
